// The apartment choice in the admin's New / Edit customer form.
//
// A customer adding an address in a store that uses serviceable apartments
// picks their apartment and types only the flat number (AddressDialog). The
// admin's customer form could only type a street address. These helpers give
// that form the same choice, and decide what is sent:
//
//   * a store that does not use apartments (`apartments_enabled` false — every
//     store that never switched them on, and one that has no active apartment)
//     sends exactly what the form has always sent;
//   * an apartment address sends the apartment and the flat number, never the
//     street fields — the server takes street, city, pincode and coordinates
//     from the apartment, as it does for a customer;
//   * turning an apartment address back into a typed one says so
//     (`address_type: "manual"`), so the apartment link is removed instead of
//     being left under a retyped street.
//
// Unlike a customer, an admin can always choose "Other address", also in a
// store whose customers may only pick listed apartments: the admin form has
// always taken any address.
//
// Pure functions, no React — covered by __tests__/adminCustomerAddress.test.js.

/** What the form works from until the store's own answer has arrived, and if
 *  it never does: the typed form, as it always was. */
export const NO_APARTMENTS = Object.freeze({ apartments: [], apartments_enabled: false, manual_allowed: true, require_flat: false });

/** GET /api/admin/customers/address-options → the three things the form uses. */
export function addressOptions(data) {
  return {
    apartments: Array.isArray(data?.apartments) ? data.apartments : [],
    apartments_enabled: !!data?.apartments_enabled,
    manual_allowed: data?.manual_allowed !== false,
    // The store requires a flat / house number on every address (typed
    // addresses get their own field) — models.Tenant.require_flat_number.
    require_flat: data?.require_flat === true,
  };
}

/** Same rule as the customer's address form: an apartment address reopens on
 *  its apartment, a typed address reopens typed, and a new address starts on
 *  whichever the store offers. */
export function initialMode(options, address) {
  if (!options?.apartments_enabled) return "manual";
  if (address?.apartment_id) return "apartment";
  if (address && (address.line1 || address.city || address.pincode)) return "manual";
  return "apartment";
}

/** The apartment an address points at. If it has since been switched off it
 *  is no longer in the list: fall back to the name kept on the address. */
export function selectedApartment(options, address) {
  if (!address?.apartment_id) return null;
  return (options?.apartments || []).find((a) => a.id === address.apartment_id)
    || { id: address.apartment_id, name: address.apartment_name || "Selected apartment" };
}

/** The picker's state when a form opens. `touched` turns true on the admin's
 *  first choice, so a late-arriving list never overrides it. */
export function startAddress(options, address) {
  return {
    mode: initialMode(options, address),
    apartment: selectedApartment(options, address),
    flat: address?.flat_number || "",
    touched: false,
  };
}

export function isApartmentMode(options, picker) {
  return !!options?.apartments_enabled && picker?.mode === "apartment";
}

/** In a store that requires a flat number: a typed address being saved
 *  (created, or any of its fields changed) needs its flat / house number. */
export function typedFlatError(options, picker, form, addressBeingSaved) {
  if (!options?.require_flat || isApartmentMode(options, picker) || !addressBeingSaved) return null;
  return (form.address_typed_flat || "").trim() ? null : "Please enter the flat / house number";
}

/** What stops a save, or null. An address is optional in both forms, so an
 *  untouched picker is fine; half of an apartment address is not. */
export function addressError(options, picker) {
  if (!isApartmentMode(options, picker)) return null;
  const flat = (picker.flat || "").trim();
  if (!picker.apartment && !flat) return null;
  if (!picker.apartment) return "Please select the apartment";
  if (!flat) return "Please enter the flat / house number";
  return null;
}

const STREET_FIELDS = ["address_line1", "address_line2", "address_city", "address_pincode"];

/** A typed address's own flat / house number (form key `address_typed_flat`),
 *  sent as `address_flat_number` with `address_type: "manual"` — the server
 *  reads a flat number without that as a half-filled apartment picker. */
function addTypedFlat(payload, flat) {
  payload.address_flat_number = flat;
  payload.address_type = "manual";
}

/** POST /api/admin/customers. */
export function createPayload(form, options, picker) {
  const payload = { ...form };
  const typedFlat = (payload.address_typed_flat || "").trim();
  delete payload.address_typed_flat;
  if (!payload.route_id) delete payload.route_id;
  if (!isApartmentMode(options, picker)) {
    if (typedFlat && payload.address_line1) addTypedFlat(payload, typedFlat);
    return payload;
  }
  STREET_FIELDS.forEach((k) => delete payload[k]);
  if (picker.apartment) {
    payload.address_apartment_id = picker.apartment.id;
    payload.address_flat_number = (picker.flat || "").trim();
  }
  return payload;
}

// [form field, address field, what an address without it counts as]
const EDIT_FIELDS = [
  ["address_label", "label", "Home"],
  ["address_line1", "line1", ""],
  ["address_line2", "line2", ""],
  ["address_city", "city", ""],
  ["address_pincode", "pincode", ""],
  ["address_landmark", "landmark", ""],
];

/** The address part of PATCH /api/admin/customers/{id}: only what changed.
 *  `original` is the customer's first address as loaded ({} / undefined for
 *  none). */
export function editAddressPayload(original, form, options, picker) {
  const addr = original || {};
  const payload = {};
  const changed = ([formKey, key, fallback]) => (form[formKey] || "") !== (addr[key] || fallback);

  if (isApartmentMode(options, picker)) {
    EDIT_FIELDS
      .filter(([, key]) => key === "label" || key === "landmark")
      .filter(changed)
      .forEach(([formKey]) => { payload[formKey] = form[formKey] || ""; });
    const flat = (picker.flat || "").trim();
    if (picker.apartment && (picker.apartment.id !== (addr.apartment_id || null) || flat !== (addr.flat_number || ""))) {
      payload.address_apartment_id = picker.apartment.id;
      payload.address_flat_number = flat;
    }
    return payload;
  }

  EDIT_FIELDS.filter(changed).forEach(([formKey]) => { payload[formKey] = form[formKey] || ""; });
  // The typed address's own flat (an apartment's flat is not a typed one's).
  const typedFlat = (form.address_typed_flat || "").trim();
  const storedFlat = addr.apartment_id ? "" : (addr.flat_number || "");
  if (typedFlat !== storedFlat) addTypedFlat(payload, typedFlat);
  // A typed address that still carries an apartment link: the admin chose
  // "Other address", or retyped the street where apartments are no longer
  // offered. Say so, and the link goes.
  const retyped = STREET_FIELDS.some((k) => k in payload);
  const choseTyped = !!options?.apartments_enabled && picker?.mode === "manual";
  if (addr.apartment_id && (retyped || choseTyped)) payload.address_type = "manual";
  return payload;
}

// Subscription statuses that still make deliveries (subscription_materializer
// GENERATING_STATUSES).
const DELIVERING = ["active", "paused", "vacation"];

/** The customer's saved addresses as the Edit dialog offers them, each with
 *  how many live subscriptions deliver there — a subscription goes to the
 *  address it names, or to the first address when it names none / one that
 *  is gone (the same rule the delivery generator uses). */
export function addressChoices(addresses, subscriptions) {
  const list = (addresses || []).filter((a) => a && a.id);
  const ids = new Set(list.map((a) => a.id));
  const counts = {};
  (subscriptions || [])
    .filter((s) => DELIVERING.includes(s?.status))
    .forEach((s) => {
      const to = ids.has(s.address_id) ? s.address_id : list[0]?.id;
      if (to) counts[to] = (counts[to] || 0) + 1;
    });
  return list.map((a) => {
    const n = counts[a.id] || 0;
    const where = a.line1 || a.apartment_name || "No street saved";
    const subs = n ? ` — ${n} subscription${n === 1 ? "" : "s"}` : "";
    return { id: a.id, text: `${a.label || "Address"} · ${where}${a.is_default ? " (default)" : ""}${subs}`, subscriptions: n };
  });
}
